import { LOCALES, type Locale } from '@aysar/contracts';
import { AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/utils/cn/cn.util';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../Tabs/Tabs';

import { type LocaleTabsProps } from './LocaleTabs.types';

/**
 * The per-locale editor tabs W7 requires on every translatable entity.
 *
 * Two decisions worth keeping:
 *
 * 1. **Endonyms.** Each tab is labelled in its OWN language — العربية and
 *    English — in both directions. Translating a language name ("Arabic" /
 *    "الإنجليزية") is the mistake that makes a language picker unusable to the
 *    very person who needs it.
 * 2. **`invalidLocales` is a prop, not internal state.** The form owns
 *    validation; this component only shows where the problem is. That is what
 *    lets `DynamicForm`'s `onInvalid` handler jump straight to the offending
 *    tab instead of leaving Save looking broken.
 * 3. **ONE panel, with a STABLE `key`** (P10.2d). The panel now holds the whole
 *    form rather than a per-locale slice of it, so the fields keep the order
 *    their author wrote. The literal `key` is load-bearing and must not be
 *    "tidied" into the locale: keyed by locale, React would tear down the entire
 *    panel on every switch and take with it the focus, the scroll position and
 *    every control's in-progress draft (a half-typed hex, a key/value row being
 *    added). Keyed stably, only the translatable controls — which `DynamicForm`
 *    keys by locale itself — remount, which is exactly the set that should.
 */
export function LocaleTabs({
  locales = LOCALES,
  locale,
  onLocaleChange,
  invalidLocales = [],
  children,
  label,
  className,
}: LocaleTabsProps) {
  const { t } = useTranslation('uiKit');

  return (
    <Tabs
      value={locale}
      onValueChange={(next) => {
        onLocaleChange(next as Locale);
      }}
      className={cn('flex flex-col gap-lg', className)}
    >
      <TabsList aria-label={label}>
        {locales.map((each) => {
          const isInvalid = invalidLocales.includes(each);
          return (
            <TabsTrigger
              key={each}
              value={each}
              // A data attribute rather than a class, so the marker is queryable
              // in a spec and styleable without a second source of truth.
              data-invalid={isInvalid ? 'true' : undefined}
              className={cn(isInvalid && 'text-destructive')}
            >
              {t(`locale.${each}`)}
              {isInvalid ? <AlertCircle aria-hidden className="h-3.5 w-3.5" /> : null}
            </TabsTrigger>
          );
        })}
      </TabsList>

      {/* ONE panel, keyed by a literal — see decision 3 in the doc comment. Its
          `value` follows the active locale so Radix keeps it mounted, and the
          form inside it is the same DOM across a switch. */}
      <TabsContent key="locale-panel" value={locale} className="mt-0">
        {children}
      </TabsContent>
    </Tabs>
  );
}
